import React, { useMemo, useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import ButtonNew from 'components/inputs/ButtonNew';
import {
  SettingsDropdown,
  SettingsMultiRange,
  SettingsControlLabel,
} from 'components/SettingsNew';
import './ConnectionInput.scss';

const ConnectionInput = ({
  matrix,
  onAddConnections,
  connections,
  activeConnection,
  onUpdateConnection,
}) => {
  const [fromNode, setFromNode] = useState(null);
  const [toNode, setToNode] = useState(null);
  const [variance, setVariance] = useState(0.5);
  const [strengthRange, setStrengthRange] = useState([0, 1]);

  const nodes = useMemo(() => matrix.nodes, [matrix]);

  const fromNodeOptions = useMemo(() => {
    return Object.values(nodes)
      .filter(({ id }) => {
        let remainingInfluences = Object.keys(nodes[id]?.influences);
        const existingConnections = connections
          .filter(n => n.fromNode === id)
          .map(n => n.toNode);
        remainingInfluences = remainingInfluences.filter(
          n => !existingConnections.includes(n)
        );
        return !!remainingInfluences.length;
      })
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(({ id, name }) => ({ label: name, value: id }));
  }, [nodes, connections]);

  const toNodeOptions = useMemo(() => {
    return Object.values(nodes)
      .filter(({ id }) => {
        const isInfluenced = nodes[fromNode]?.influences[id] !== undefined;
        const alreadyConnected = connections.find(
          node => node.fromNode === fromNode && node.toNode === id
        );
        return isInfluenced && !alreadyConnected;
      })
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(({ id, name }) => ({ label: name, value: id }));
  }, [fromNode, nodes, connections]);

  const disableAddConnections = useMemo(() => {
    if (!fromNode || !toNode) return true;
    return connections.some(
      node => node.fromNode === fromNode && node.toNode === toNode
    );
  }, [connections, fromNode, toNode]);

  const atanhify = useCallback((influence, variance) => {
    if (isNaN(influence)) return;

    // atanh cannot handle 1 or -1
    influence = Math.max(influence, -0.999);
    influence = Math.min(influence, 0.999);

    // z
    const z = Math.atanh(influence);

    // z_range
    const calculatedVariance = z * variance;
    let high = z + calculatedVariance;
    let low = z - calculatedVariance;

    // Bring back after setting variance
    low = Math.tanh(low);
    high = Math.tanh(high);

    return [low, high];
  }, []);

  const getStrengthRange = (fromId, toId, variance) => {
    const node = matrix?.nodes?.[fromId];
    // r
    let influence = Number(node?.influences?.[toId]);

    return atanhify(influence, variance);
  };

  useEffect(() => {
    if (!fromNode || !toNode) return;
    const existingConnection = connections.find(
      c => c.fromNode === fromNode && c.toNode === toNode
    );
    const strengthRange = existingConnection
      ? existingConnection.strengthRange
      : getStrengthRange(fromNode, toNode, 0.5);

    const variance = existingConnection ? existingConnection.variance : 0.5;

    if (!strengthRange) return;
    setVariance(variance);
    setStrengthRange(strengthRange);
  }, [matrix, toNode, fromNode, connections]);

  const onImportAllConnections = () => {
    const centralNode = fromNode;
    const influencedNodes = Object.keys(
      matrix?.nodes?.[centralNode]?.influences ?? {}
    );
    const connectionsOut = influencedNodes
      .map(nodeId => {
        if (nodeId === centralNode) return false;
        const strengthRange = getStrengthRange(centralNode, nodeId, 0.5);
        return {
          fromNode: centralNode,
          toNode: nodeId,
          strengthRange,
          variance: 0.5,
        };
      })
      .filter(Boolean);

    const influencerNodes = Object.entries(matrix?.nodes).reduce(
      (acc, [k, v]) => {
        if (v?.influences?.[centralNode]) {
          acc.push(k);
        }
        return acc;
      },
      []
    );
    const connectionsIn = influencerNodes.map(nodeId => {
      const strengthRange = getStrengthRange(nodeId, centralNode, 0.5);
      return {
        fromNode: nodeId,
        toNode: centralNode,
        strengthRange,
        variance: 0.5,
      };
    });

    onAddConnections(connectionsOut.concat(connectionsIn));
  };

  useEffect(() => {
    if (activeConnection) {
      const {
        fromNode: from,
        toNode: to,
        strengthRange: strength,
      } = activeConnection;
      setFromNode(from);
      setToNode(to);
      setStrengthRange(strength);
    }
  }, [activeConnection]);

  useEffect(() => {
    if (
      !fromNodeOptions.map(o => o.value).includes(fromNode) &&
      activeConnection?.fromNode !== fromNode
    ) {
      setFromNode(null);
    }
    if (
      !toNodeOptions.map(o => o.value).includes(toNode) &&
      activeConnection?.toNode !== toNode
    ) {
      setToNode(null);
    }
  }, [fromNodeOptions, toNodeOptions, fromNode, toNode]);

  const varianceOptions = useMemo(() => {
    return [
      { label: '10%', value: 0.1 },
      { label: '20%', value: 0.2 },
      { label: '30%', value: 0.3 },
      { label: '40%', value: 0.4 },
      { label: '50%', value: 0.5 },
      { label: '60%', value: 0.6 },
      { label: '70%', value: 0.7 },
      { label: '80%', value: 0.8 },
      { label: '90%', value: 0.9 },
    ];
  }, []);

  return (
    <div className="ConnectionInput">
      <SettingsDropdown
        label="Select first node"
        options={fromNodeOptions}
        value={fromNode}
        onChange={setFromNode}
      />
      <ButtonNew
        buttonClasses={classnames('secondary-button-small', {
          disabled:
            !fromNode || !fromNodeOptions.some(n => n.value === fromNode),
        })}
        iconLeft="plus"
        label="Import all connections"
        onClick={onImportAllConnections}
      />
      <div className="ConnectionInput-alternative-description-container">
        <div className="ConnectionInput-alternative-line" />
        <div className="ConnectionInput-alternative-description">
          or add connections manually
        </div>
        <div className="ConnectionInput-alternative-line" />
      </div>
      <SettingsDropdown
        label="Select connection"
        disabled={!fromNode}
        options={toNodeOptions}
        value={toNode}
        onChange={setToNode}
      />

      <SettingsDropdown
        label="Select range of variation in interaction strength"
        disabled={!(fromNode && toNode)}
        options={varianceOptions}
        value={variance}
        onChange={v => {
          setVariance(v);
          setStrengthRange(getStrengthRange(fromNode, toNode, v));
        }}
      />
      <ButtonNew
        buttonClasses={classnames('secondary-button-small', {
          disabled: !activeConnection && disableAddConnections,
        })}
        iconLeft="plus"
        label={`${activeConnection ? `Save` : `Add`} connection`}
        onClick={() => {
          activeConnection
            ? onUpdateConnection({ fromNode, toNode, strengthRange, variance })
            : onAddConnections([{ fromNode, toNode, strengthRange, variance }]);
        }}
      />
    </div>
  );
};

export default ConnectionInput;
